import { config } from '@/config/env';
import { authClient } from '@/lib/authClient';

interface ApiRequestOptions extends RequestInit {}

const apiUtils = {
  baseUrl: `${config.apiUrl}/api`,

  handleErrorResponse: async (response: Response) => {
    const { status } = response;

    if (status === 401) {
      try {
        await authClient.signOut();
      } catch {}

      window.location.href = '/';

      throw new Error('Authentication failed');
    }

    if (status === 403) {
      throw new Error('Access denied');
    }

    let errorMessage = `Request failed with status ${status}`;

    try {
      const errorData = (await response.json()) as { error?: string; message?: string };

      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } catch {
      switch (status) {
        case 404:
          errorMessage = 'The requested resource was not found';
          break;
        case 500:
          errorMessage = 'Internal server error. Please try again later';
          break;
        case 0:
          errorMessage = 'Network error. Please check your connection';
          break;
        default:
          errorMessage = `Request failed with status ${status}`;
      }
    }

    throw new Error(errorMessage);
  },

  makeRequest: async <T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> => {
    const { ...requestOptions } = options;
    const url = `${apiUtils.baseUrl}${endpoint}`;

    const headers = new Headers(requestOptions.headers ?? {});
    headers.set('Accept', 'application/json');

    if (requestOptions.body && typeof requestOptions.body === 'string' && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    const response = await fetch(url, {
      ...requestOptions,
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      await apiUtils.handleErrorResponse(response);
    }

    return response.json() as Promise<T>;
  },
};

export const apiClient = {
  get: <T>(endpoint: string, options?: ApiRequestOptions): Promise<T> => {
    return apiUtils.makeRequest<T>(endpoint, { ...options, method: 'GET' });
  },

  post: <T>(endpoint: string, data?: unknown, options?: ApiRequestOptions): Promise<T> => {
    return apiUtils.makeRequest<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  put: <T>(endpoint: string, data?: unknown, options?: ApiRequestOptions): Promise<T> => {
    return apiUtils.makeRequest<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  delete: <T>(endpoint: string, options?: ApiRequestOptions): Promise<T> => {
    return apiUtils.makeRequest<T>(endpoint, { ...options, method: 'DELETE' });
  },
};
